import React, { useRef, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import CaretDown from 'web-ui-kit/assets/CaretDown';
import { primaryM, surfaceM } from 'web-ui-kit/colors';
import { Header5LargeStyles, BodySmallStyles, BodyLargeStyles, BodyBoldSmallStyles, BodyBoldLargeStyles } from 'web-ui-kit/typography';
import { useSafeResizeEventListener } from 'web-ui-kit/hooks';
import { MQ_FOR_TABLET_LANDSCAPE_UP } from 'web-ui-kit/media';
import { isTruncated, truncateLines } from 'web-ui-kit/styleUtils';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

function LongParagraph(_ref) {
  var _ref$id = _ref.id,
      id = _ref$id === void 0 ? '' : _ref$id,
      _ref$heading = _ref.heading,
      heading = _ref$heading === void 0 ? '' : _ref$heading,
      _ref$lineTruncateCnt = _ref.lineTruncateCnt,
      lineTruncateCnt = _ref$lineTruncateCnt === void 0 ? 4 : _ref$lineTruncateCnt,
      _ref$paragraph = _ref.paragraph,
      paragraph = _ref$paragraph === void 0 ? '' : _ref$paragraph;
  var paragraphRef = useRef(); // Truncation state. One of:
  // ['initial', 'not-needed', 'disabled', 'truncated']

  var _useState = useState('initial'),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1]; // This callback is fired whenever we need to update the truncation state.


  var updateIsTruncated = useCallback(function () {
    var paragraphIsTruncated = isTruncated(paragraphRef.current);
    setState(paragraphIsTruncated ? 'truncated' : 'not-needed');
  }, [paragraphRef, setState]); // We do not know in advance if the paragraph will require truncation.
  // Thus, once the component renders we compute whether or not
  // the paragraph text has actually been truncated at the current viewport size.

  useEffect(function () {
    if (state === 'initial') {
      updateIsTruncated();
    }
  }, [state, updateIsTruncated]); //  We need to re-compute whether or not the paragraph
  // needs truncation on resize events.

  useSafeResizeEventListener({
    listen: state === 'not-needed' || state === 'truncated',
    onResize: updateIsTruncated
  }); // When user clicks on the "More" button, we want to
  // manually set the disabled truncation states.

  var handleDisableTruncation = function handleDisableTruncation() {
    setState('disabled');
  };

  var Container = styles.Container,
      Heading = styles.Heading,
      MoreArrow = styles.MoreArrow,
      MoreLessButton = styles.MoreLessButton,
      Paragraph = styles.Paragraph;
  return (
    /*#__PURE__*/
    React.createElement(Container, {
      "data-testid": id
    },
    /*#__PURE__*/
    React.createElement(Heading, null, heading),
    /*#__PURE__*/
    React.createElement(Paragraph, {
      applyTruncationStyles: ['initial', 'truncated'].includes(state),
      "data-testid": "paragraph-".concat(state),
      lineTruncateCnt: lineTruncateCnt,
      ref: paragraphRef
    }, paragraph), state === 'truncated' &&
    /*#__PURE__*/
    React.createElement(MoreLessButton, {
      onClick: handleDisableTruncation
    }, "More",
    /*#__PURE__*/
    React.createElement(MoreArrow, {
      color: primaryM
    })))
  );
}

var styles = {
  Container: styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  "]))),
  Heading: styled.p(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", ";\n  "])), Header5LargeStyles),
  Paragraph: styled.p(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    ", ";\n    white-space: pre-wrap;\n\n    ", " {\n      ", ";\n    }\n\n    ", "}\n  "])), BodySmallStyles, MQ_FOR_TABLET_LANDSCAPE_UP, BodyLargeStyles, function (p) {
    return p.applyTruncationStyles && truncateLines({
      lines: p.lineTruncateCnt
    });
  }),
  MoreLessButton: styled.button(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    ", ";\n\n    appearance: none;\n    border: none;\n    outline: none;\n    padding: 0;\n\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n\n    background-color: ", ";\n    color: ", ";\n    cursor: pointer;\n    margin-top: 0.125rem;\n\n    ", " {\n      ", ";\n      color: ", ";\n      margin-top: 0.125rem;\n    }\n  "])), BodyBoldSmallStyles, surfaceM, primaryM, MQ_FOR_TABLET_LANDSCAPE_UP, BodyBoldLargeStyles, primaryM),
  MoreArrow: styled(CaretDown)(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    margin-left: 0.25rem;\n  "])))
};
export default LongParagraph;